import { SocioRow } from '../../constitution-form/models/socio-row';
import { ConstitutionSnapshot, ShareNumberingSnapshot, SocioSnapshot } from '../models/constitution-snapshot.model';

type ShareNumbering = NonNullable<SocioRow['shareNumberings']>[number];

/** Numbering range amortized by a reducción de capital. `seriesId` is null when the capital has no series. */
export interface AmortizacionRango {
  seriesId: number | null;
  desde: number;
  hasta: number;
}

/** What a socio loses in an amortización. */
export interface SocioAfectado {
  nombre: string;
  nif: string;
  associatedClientId: number | null;
  shareNumberings: Array<ShareNumbering & { nominalValue?: number | null }>;
}

function withTotals(socio: SocioRow, shareNumberings: ShareNumbering[]): SocioRow {
  return {
    ...socio,
    shareNumberings,
    sharesCount: shareNumberings.reduce((sum, n) => sum + (n.sharesCount ?? 0), 0),
  };
}

function sameSeries(a: number | null | undefined, b: number | null | undefined): boolean {
  return (a ?? null) === (b ?? null);
}

function piece(n: ShareNumbering, from: number, to: number): ShareNumbering {
  return { ...n, sharesNumberFrom: from, sharesNumberTo: to, sharesCount: to - from + 1 };
}

/** Splits a numbering into the parts outside (`kept`) and inside (`removed`) the amortized ranges. */
function splitNumbering(
  n: ShareNumbering,
  rangos: AmortizacionRango[],
): { kept: ShareNumbering[]; removed: ShareNumbering[] } {
  const relevant = rangos
    .filter(r => sameSeries(r.seriesId, n.seriesId) && Number.isFinite(r.desde) && Number.isFinite(r.hasta) && r.hasta >= r.desde)
    .sort((a, b) => a.desde - b.desde);

  const kept: ShareNumbering[] = [];
  const removed: ShareNumbering[] = [];
  let cursor = n.sharesNumberFrom;

  for (const r of relevant) {
    const from = Math.max(r.desde, n.sharesNumberFrom, cursor);
    const to = Math.min(r.hasta, n.sharesNumberTo);
    if (to < from) continue;
    if (from > cursor) kept.push(piece(n, cursor, from - 1));
    removed.push(piece(n, from, to));
    cursor = to + 1;
  }
  if (cursor <= n.sharesNumberTo) kept.push(piece(n, cursor, n.sharesNumberTo));
  return { kept, removed };
}

/**
 * Applies an amortización to the socios: holdings of the removed series disappear and the amortized
 * ranges are subtracted from the rest (a range in the middle of a holding splits it in two).
 * A socio left without holdings leaves the list.
 */
export function applyAmortizacion(
  socios: SocioRow[],
  rangos: AmortizacionRango[],
  removedSeriesIds: Set<number>,
): SocioRow[] {
  return socios.flatMap(socio => {
    const numberings = socio.shareNumberings ?? [];
    if (numberings.length === 0) return [socio];

    const remaining = numberings
      .filter(n => !(n.seriesId != null && removedSeriesIds.has(n.seriesId)))
      .flatMap(n => splitNumbering(n, rangos).kept);

    return remaining.length === 0 ? [] : [withTotals(socio, remaining)];
  });
}

/** Inverse of `applyAmortizacion`: the holdings each socio loses, from the socios before the operation. */
export function extractAmortizedShares(
  socios: SocioRow[],
  rangos: AmortizacionRango[],
  removedSeriesIds: Set<number>,
  /** VN de la serie de cada numeración antes de la operación: se guarda porque una serie dada de baja ya no está en el capital. */
  nominalDe?: (n: ShareNumbering) => number | null | undefined,
): SocioAfectado[] {
  const result: SocioAfectado[] = [];
  for (const socio of socios) {
    const lost = (socio.shareNumberings ?? []).flatMap(n =>
      n.seriesId != null && removedSeriesIds.has(n.seriesId) ? [n] : splitNumbering(n, rangos).removed,
    );
    if (lost.length === 0) continue;
    result.push({
      nombre: socio.name,
      nif: socio.documentNumber,
      associatedClientId: socio.associatedClient?.id ?? null,
      shareNumberings: nominalDe ? lost.map(n => ({ ...n, nominalValue: nominalDe(n) ?? null })) : lost,
    });
  }
  return result;
}

/**
 * For a reducción de capital, keeps in each partner only the shares that were amortized (read from
 * `sociosAfectados` stored in the operation). Returns the snapshot unchanged for operations saved
 * without that information.
 */
export function restrictSociosToAmortizedShares(
  snapshot: ConstitutionSnapshot,
  operationData: Record<string, unknown> | null | undefined,
): ConstitutionSnapshot {
  const reducciones = Array.isArray(operationData?.['reducciones'])
    ? operationData['reducciones'] as Array<Record<string, unknown>>
    : [];
  const afectados = reducciones.flatMap(r =>
    Array.isArray(r['sociosAfectados']) ? r['sociosAfectados'] as SocioAfectado[] : []);
  if (afectados.length === 0) return restrictSociosToVnSeries(snapshot, reducciones);

  const byKey = new Map<string, SocioSnapshot>();
  for (const a of afectados) {
    const key = a.associatedClientId != null ? `c${a.associatedClientId}` : `n${a.nif || a.nombre}`;
    const numberings: ShareNumberingSnapshot[] = a.shareNumberings.map(n => ({
      seriesId: n.seriesId,
      seriesName: n.seriesName ?? '',
      sharesCount: n.sharesCount,
      sharesNumberFrom: n.sharesNumberFrom,
      sharesNumberTo: n.sharesNumberTo,
      titulo: n.titulo ?? '',
      ownershipPercentage: n.ownershipPercentage ?? null,
      nominalValue: n.nominalValue ?? null,
    }));
    const existing = byKey.get(key);
    if (existing) {
      existing.shareNumberings = [...(existing.shareNumberings ?? []), ...numberings];
      existing.numAcciones = (existing.shareNumberings ?? []).reduce((sum, n) => sum + n.sharesCount, 0);
      continue;
    }
    const original = (snapshot.socios ?? []).find(s =>
      (a.nif && s.nif === a.nif) || s.nombre === a.nombre);
    byKey.set(key, {
      ...(original ?? { nombre: a.nombre, nif: a.nif, titulo: '', porcentajeParticipacion: '' }),
      numAcciones: numberings.reduce((sum, n) => sum + n.sharesCount, 0),
      shareNumberings: numberings,
    } as SocioSnapshot);
  }
  return { ...snapshot, socios: [...byKey.values()] };
}

/**
 * For a reducción (or escisión) with series, keeps in the capital table only the series its entries
 * touch. Unchanged for a capital without series, when no entry names a serie, or when none of them
 * is still in the capital (e.g. a serie that was given de baja).
 */
export function restrictCapitalToReducedSeries(
  snapshot: ConstitutionSnapshot,
  operationData: Record<string, unknown> | null | undefined,
): ConstitutionSnapshot {
  const capital = snapshot.capitalSocial;
  const reducciones = Array.isArray(operationData?.['reducciones'])
    ? operationData['reducciones'] as Array<Record<string, unknown>>
    : [];
  if (!capital?.hasSeries || !Array.isArray(capital.series) || reducciones.length === 0) return snapshot;

  const ids = new Set(reducciones.map(r => Number(r['serie'])).filter(n => Number.isFinite(n) ));
  const names = new Set(reducciones
    .map(r => String(r['serieNombre'] ?? '').trim().toLowerCase())
    .filter(n => n !== ''));
  const affected = capital.series.filter(s =>
    names.has(String(s.nombre ?? '').trim().toLowerCase()) || (s.id != null && ids.has(Number(s.id))));
  return affected.length === 0 ? snapshot : { ...snapshot, capitalSocial: { ...capital, series: affected } };
}

/** Reducción solo de VN: no hay acciones amortizadas, así que los socios se limitan a las series cuyo VN baja. */
function restrictSociosToVnSeries(
  snapshot: ConstitutionSnapshot,
  reducciones: Array<Record<string, unknown>>,
): ConstitutionSnapshot {
  const vn = reducciones.filter(r => r['tipoReduccion'] === 'disminucion_vn');
  if (vn.length === 0 || vn.length !== reducciones.length || !Array.isArray(snapshot.socios)) return snapshot;
  const names = new Set(vn.map(r => String(r['serieNombre'] ?? '').trim().toLowerCase()).filter(n => n !== ''));
  const ids = new Set(vn.map(r => Number(r['serie'])).filter(n => Number.isFinite(n) && n !== 0));
  if (names.size === 0 && ids.size === 0) return snapshot;

  const socios = snapshot.socios.flatMap(socio => {
    const numberings = (socio.shareNumberings ?? []).filter(n =>
      names.has(String(n.seriesName ?? '').trim().toLowerCase()) || (n.seriesId != null && ids.has(Number(n.seriesId))));
    return numberings.length === 0 ? [] : [{
      ...socio,
      shareNumberings: numberings,
      numAcciones: numberings.reduce((sum, n) => sum + n.sharesCount, 0),
    }];
  });
  return { ...snapshot, socios };
}

/**
 * Operaciones guardadas antes de registrar el VN de lo amortizado: lo recupera del capital anterior a la operación
 * (la serie dada de baja ya no está en el capital de la propia operación). No toca las numeraciones que ya traen VN.
 */
export function completarVnDeAmortizadas(
  snapshot: ConstitutionSnapshot,
  capitalPrevio: ConstitutionSnapshot['capitalSocial'] | null | undefined,
): ConstitutionSnapshot {
  if (!capitalPrevio || !Array.isArray(snapshot.socios)) return snapshot;
  const nominalDe = (n: ShareNumberingSnapshot): number | null => {
    if (!capitalPrevio.hasSeries) return capitalPrevio.valorNominal ?? null;
    const nombre = String(n.seriesName ?? '').trim().toLowerCase();
    const serie = capitalPrevio.series.find(s =>
      (n.seriesId != null && s.id != null && Number(s.id) === Number(n.seriesId))
      || (nombre !== '' && String(s.nombre ?? '').trim().toLowerCase() === nombre));
    return serie ? Number(serie.valorNominal) || 0 : null;
  };
  return {
    ...snapshot,
    socios: snapshot.socios.map(socio => ({
      ...socio,
      shareNumberings: socio.shareNumberings?.map(n => n.nominalValue != null ? n : { ...n, nominalValue: nominalDe(n) }),
    })),
  };
}
